import React, { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Box,
  Stack,
  Button,
  IconButton,
  Typography,
  Avatar,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { green } from "@mui/material/colors";
import Item from "../../Template/Item";
import WithPermission from "../../components/WithPermission";
import { TypeOf } from "zod";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { getProfile, updateProfile } from "../../redux/actions/profile.actions";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

type FieldsName = {
  field_label: string;
  field_slug: string;
  field_type: string;
  required_field: string;
  field_options?: string;
  file_extensions?: any;
}[];

type customFieldsPair = {
  info_value: any;
  customfield: number;
}[];

export default function Profile() {
  const dispatch = useDispatch();
  const [pfields, setPfields] = useState<FieldsName>([]);
  const [pextensions, setPextensions] = useState([]);

  const useCustomFieldsDynamicSchema = (schema: any, config: any) => {
    const { field_slug, validationType, validations = [] } = config;
    if (!yup[validationType as keyof typeof yup]) {
      return schema;
    }
    const yupMethod = validationType as keyof typeof yup;
    let validator = yup[yupMethod] as Function;
    validator = validator();

    validations.forEach((validation: any) => {
      const { params, type } = validation;
      if (!validator[type as keyof typeof validator]) {
        return;
      }
      validator = validator[type as keyof typeof validator](...params);
    });
    schema[field_slug] = validator;
    return schema;
  };

  const useCustomFieldsExtendValidation = (customFields: any) => {
    return customFields.map((customField: any) => {
      switch (customField.field_type) {
        case "text":
        case "textarea":
        case "select":
          if (Number(customField.required_field) === 1) {
            return {
              ...customField,
              validationType: "string",
              validations: [
                {
                  type: "required",
                  params: ["This field is required"],
                },
              ],
            };
          } else {
            return customField;
          }
        case "multiple":
          if (Number(customField.required_field) === 1) {
            return {
              ...customField,
              validationType: "array",
              validations: [
                {
                  type: "min",
                  params: [1, "This field is required"],
                },
              ],
            };
          } else {
            return customField;
          }

        case "file":
          if (Number(customField.required_field) === 1) {
            return {
              ...customField,
              validationType: "mixed",
              validations: [
                {
                  type: "test",
                  params: [
                    "required",
                    "File is required",
                    (file: any) => {
                      return file.length >= 1;
                    },
                  ],
                },
                {
                  type: "test",
                  params: [
                    "fileSize",
                    "File size too large, max file size is 1 Mb",
                    (file: any) => {
                      return file.length >= 1 && file[0].size <= 1100000;
                    },
                  ],
                },
                {
                  type: "test",
                  params: [
                    "fileType",
                    "Incorrect file type",
                    (file: any) => {
                      return (
                        file.length >= 1 &&
                        pextensions.includes(file[0].type as never)
                      );
                    },
                  ],
                },
              ],
            };
          } else {
            return customField;
          }

        default:
          return customField;
      }
    });
  };

  const dynamicFormData = useCustomFieldsExtendValidation(pfields);
  const customFieldsSchema = dynamicFormData.reduce(
    useCustomFieldsDynamicSchema,
    {}
  );

  const dynamicValidationSchema = yup.object().shape(customFieldsSchema);

  const {
    formState: { errors },
    register,
    handleSubmit,
    control,
    reset,
  } = useForm<TypeOf<typeof customFieldsSchema>>({
    defaultValues: {},
    resolver: yupResolver(dynamicValidationSchema), // Here we use our dynamic schema
    mode: "onTouched",
  });

  const profileFields: FieldsName = useMemo(
    () => [
      {
        field_label: "Full Name",
        field_slug: "fullname",
        field_type: "text",
        required_field: "1",
      },
      {
        field_label: "Email Address",
        field_slug: "email",
        field_type: "text",
        required_field: "1",
      },
      {
        field_label: "Date of Birth",
        field_slug: "date_of_birth",
        field_type: "date",
        required_field: "1",
      },
      {
        field_slug: "address",
        field_label: "Address",
        field_type: "text",
        required_field: "1",
      },
      {
        field_slug: "mobile_number",
        field_label: "Mobile Number",
        field_type: "text",
        required_field: "1",
      },
    ],
    []
  );
  const fileTypes: any = useMemo(() => [], []);

  const { profile, save } = useSelector((state: any) => state.profile);
  const uiloading = useSelector((state: any) => state.UI.loading);

  useEffect(() => {
    dispatch<any>(getProfile());
  }, [dispatch]);

  useEffect(() => {
    reset(profile);
    if (profile) {
      let infosArray: any = [];
      if (profile.infos) {
        //const infos: customFieldsPair = JSON.parse(profile.infos);
        const infos: customFieldsPair = profile.infos;
        /* infos.map((c: any, i: number) => {
          infosArray[c.customfield] = c.info_value
        }) */
        infos.forEach((c) => {
          infosArray[c.customfield] = c.info_value;
        });
      }

      profile.company.customfields.forEach((opts: any) => {
        profileFields.push(opts);
        if (opts.file_extensions) {
          opts.file_extensions.split(",").forEach((ext: any) => {
            switch (ext) {
              case "jpg":
                fileTypes.push("image/jpg");
                fileTypes.push("image/jpeg");
                break;
              case "png":
                fileTypes.push("image/png");
                break;
              case "gif":
                fileTypes.push("image/png");
                break;
              case "doc":
                fileTypes.push("application/msword");
                fileTypes.push(
                  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                );
                break;
              case "odt":
                fileTypes.push("application/vnd.oasis.opendocument.text");
                break;
              case "pdf":
                fileTypes.push("application/pdf");
                break;
              case "csv":
                fileTypes.push("text/csv");
                break;
              case "text":
                fileTypes.push("text/plain");
                break;
              case "xls":
                fileTypes.push("text/vnd.ms-excel");
                fileTypes.push(
                  "text/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                );
                break;
            }
          });
        }
        if (infosArray.length > 0) {
          if (infosArray[opts.id]) {
            if (opts.field_type === "multiple") {
              infosArray[opts.id] = infosArray[opts.id].split(",");
            }

            profile[opts.field_slug] = infosArray[opts.id];
          }
        }
      });
      setPfields(profileFields);
      setPextensions(fileTypes);
    }
  }, [profile, reset, fileTypes, profileFields]);

  const onFormSubmit: SubmitHandler<TypeOf<typeof customFieldsSchema>> = (
    data
  ) => {
    const infos: customFieldsPair = [];
    /* data.company.customfields.map((cfield: any, index: number) => {
      if (data[cfield.field_slug]) {
        infos.push({ info_value: data[cfield.field_slug], customfield: cfield.id })
       
      }
      if (infos.length > 0) {
        data.infos = infos
      }

    }) */

    data.company.customfields.forEach((cfield: any) => {
      //console.log(data)

      if (data[cfield.field_slug]) {
        //console.log(data[cfield.field_slug]);

        if (data[cfield.field_slug] instanceof FileList) {
          //for (let i = 0; i < data[cfield.field_slug].length; i++) {
          for (const value of data[cfield.field_slug]) {
            //console.log(data[cfield.field_slug][i])
            //infos.push({ info_value: data[cfield.field_slug][i].name, customfield: cfield.id })

            infos.push({ info_value: value.name, customfield: cfield.id });
          }
        } else if (
          Array.isArray(data[cfield.field_slug]) &&
          data[cfield.field_slug].length > 0
        ) {
          infos.push({
            info_value: data[cfield.field_slug].join(","),
            customfield: cfield.id,
          });
        } else {
          infos.push({
            info_value: data[cfield.field_slug],
            customfield: cfield.id,
          });
        }
      }

      if (infos.length > 0) {
        data.infos = infos;
      }
    });
    //console.log(data.infos)

    dispatch<any>(updateProfile(data, profile.id));
  };

  /* console.log(pextensions)
  console.log(errors) */
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Item>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={5}
          >
            <Typography variant="h5" gutterBottom>
              Profile
            </Typography>
            <Box
              display="flex"
              justifyContent="flex-start"
              alignItems="flex-start"
            ></Box>
          </Stack>
          <Box sx={{ height: 700, width: "100%" }}>
            <WithPermission roleReqiured={["USER"]} message="Access Denied">
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Item>
                    <IconButton>
                      <Avatar
                        alt="User"
                        sx={{ width: 56, height: 56 }}
                        src="/static/images/avatar/2.jpg"
                      />
                    </IconButton>
                    <Typography variant="h6" gutterBottom component="div">
                      {profile?.fullname}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      component="div"
                    >
                      {profile?.address}
                    </Typography>
                  </Item>
                  <Divider />
                  <Item>
                    <Button>Upload Profile Picture</Button>
                  </Item>
                </Grid>
                <Grid item xs={8}>
                  <Box
                    component="form"
                    sx={{
                      "& .MuiTextField-root": { m: 1 },
                    }}
                    noValidate
                    autoComplete="off"
                    onSubmit={handleSubmit(onFormSubmit)}
                  >
                    <Item>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ textAlign: "left" }}
                      >
                        My Profile
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        component="div"
                        sx={{ textAlign: "left" }}
                      >
                        You can edit your profile
                      </Typography>
                    </Item>
                    <Divider />
                    {uiloading && (
                      <Grid item md={12} xs={12} sx={{ mt: 5 }}>
                        <Stack alignItems="center">
                          <CircularProgress />
                        </Stack>
                      </Grid>
                    )}

                    {!uiloading && (
                      <>
                        <Item>
                          <Grid container spacing={3}>
                            {pfields.length > 0 &&
                              pfields.map((pfield, index) => {
                                if (pfield.field_type === "text")
                                  return (
                                    <Grid
                                      item
                                      md={6}
                                      xs={12}
                                      key={`grid-text-${index}`}
                                    >
                                      <TextField
                                        required={
                                          Number(pfield.required_field) === 1
                                            ? true
                                            : false
                                        }
                                        id={`fields-${index}`}
                                        label={pfield.field_label}
                                        InputLabelProps={{
                                          shrink: true,
                                        }}
                                        fullWidth
                                        {...register(pfield.field_slug)}
                                        error={!!errors[pfield.field_slug]}
                                        helperText={
                                          errors[pfield.field_slug] &&
                                          errors[pfield.field_slug].message
                                        }
                                      />
                                    </Grid>
                                  );

                                if (pfield.field_type === "textarea")
                                  return (
                                    <Grid
                                      item
                                      md={6}
                                      xs={12}
                                      key={`grid-textarea-${index}`}
                                    >
                                      <TextField
                                        required={
                                          Number(pfield.required_field) === 1
                                            ? true
                                            : false
                                        }
                                        id={`fields-${index}`}
                                        label={pfield.field_label}
                                        InputLabelProps={{
                                          shrink: true,
                                        }}
                                        fullWidth
                                        multiline
                                        rows={3}
                                        {...register(pfield.field_slug)}
                                        error={!!errors[pfield.field_slug]}
                                        helperText={
                                          errors[pfield.field_slug] &&
                                          errors[pfield.field_slug].message
                                        }
                                      />
                                    </Grid>
                                  );
                                if (pfield.field_type === "select")
                                  return (
                                    <Grid
                                      item
                                      md={6}
                                      xs={12}
                                      key={`grid-select-${index}`}
                                    >
                                      <FormControl
                                        fullWidth
                                        variant="outlined"
                                        sx={{ mt: 1 }}
                                        error={!!errors[pfield.field_slug]}
                                      >
                                        <InputLabel id="role-select-label">
                                          {pfield.field_label}
                                        </InputLabel>
                                        <Controller
                                          name={pfield.field_slug}
                                          control={control}
                                          defaultValue=""
                                          render={({
                                            field: { onChange, value },
                                          }) => (
                                            <Select
                                              defaultValue="1"
                                              labelId={pfield.field_slug}
                                              className="input-container"
                                              id={pfield.field_label}
                                              label={pfield.field_label}
                                              value={value}
                                              onChange={onChange}
                                            >
                                              {pfield.field_options &&
                                                pfield.field_options
                                                  .split(",")
                                                  .map((c: any, i: number) => (
                                                    <MenuItem
                                                      key={`c-${i}`}
                                                      value={c}
                                                    >
                                                      {c}
                                                    </MenuItem>
                                                  ))}
                                            </Select>
                                          )}
                                        />
                                        <FormHelperText>
                                          {errors[pfield.field_slug]
                                            ? errors[pfield.field_slug].messag
                                            : ""}
                                        </FormHelperText>
                                      </FormControl>
                                    </Grid>
                                  );
                                if (pfield.field_type === "multiple")
                                  return (
                                    <Grid
                                      item
                                      md={6}
                                      xs={12}
                                      key={`grid-select-${index}`}
                                    >
                                      <Controller
                                        name={pfield.field_slug}
                                        control={control}
                                        defaultValue={[]}
                                        render={({ field }) => (
                                          <FormControl
                                            fullWidth
                                            variant="outlined"
                                            sx={{ mt: 1 }}
                                            error={!!errors[pfield.field_slug]}
                                          >
                                            <InputLabel id="age">
                                              {pfield.field_label}
                                            </InputLabel>
                                            <Select
                                              {...field}
                                              labelId="{field.field_slug"
                                              label="{field.field_label"
                                              multiple
                                              defaultValue={[]}
                                            >
                                              {pfield.field_options &&
                                                pfield.field_options
                                                  .split(",")
                                                  .map((c: any, i: number) => (
                                                    <MenuItem
                                                      key={`c-${i}`}
                                                      value={c}
                                                    >
                                                      {c}
                                                    </MenuItem>
                                                  ))}
                                            </Select>
                                            <FormHelperText>
                                              {errors[pfield.field_slug]
                                                ? errors[pfield.field_slug]
                                                    .messag
                                                : ""}
                                            </FormHelperText>
                                          </FormControl>
                                        )}
                                      />
                                    </Grid>
                                  );
                                if (pfield.field_type === "file")
                                  return (
                                    <Grid
                                      item
                                      md={6}
                                      xs={12}
                                      key={`grid-file-${index}`}
                                    >
                                      <TextField
                                        required={
                                          pfield.required_field === "1"
                                            ? true
                                            : false
                                        }
                                        id={`customfield-${index}`}
                                        label={pfield.field_label}
                                        type="file"
                                        InputLabelProps={{
                                          shrink: true,
                                        }}
                                        fullWidth
                                        {...register(pfield.field_slug)}
                                        error={!!errors[pfield.field_slug]}
                                        helperText={
                                          errors[pfield.field_slug] &&
                                          errors[pfield.field_slug].message
                                        }
                                      />
                                      <FormHelperText sx={{ ml: 2 }}>
                                        Allowed File Types:{" "}
                                        {pfield.file_extensions}
                                      </FormHelperText>
                                      <FormHelperText sx={{ ml: 2 }}>
                                        Max file size: 1Mb
                                      </FormHelperText>
                                    </Grid>
                                  );
                                else {
                                  return null;
                                }
                              })}
                          </Grid>
                        </Item>
                        <Item>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              p: 2,
                            }}
                          >
                            <Box sx={{ m: 1, position: "relative" }}>
                              <Button
                                type="submit"
                                variant="contained"
                                color="success"
                                startIcon={<SaveIcon />}
                                disabled={save}
                              >
                                Save
                              </Button>
                              {save && (
                                <CircularProgress
                                  size={24}
                                  sx={{
                                    color: green[500],
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    marginTop: "-12px",
                                    marginLeft: "-12px",
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Item>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </WithPermission>
          </Box>
        </Item>
      </Grid>
    </Grid>
  );
}
